import { animate, useMotionValue } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';
// 2 is because of the setup to be able to easily animate from slide to slide
export const convertIndexToSlideIndex = (index, length, isRTL = false) => {
    // dependant on line 127 at GallerySlides which halves the content.length
    const lengthWithDuplicates = length * 2;
    const currentIndex = isRTL ? 0 - index : index;
    // ensure it will loop correctly without going out of bounds
    return ((currentIndex % lengthWithDuplicates) + lengthWithDuplicates) % lengthWithDuplicates;
};
export const calculateNewSlideIndex = (direction, position, length) => {
    // 2 is because of the setup to be able to easily animate from slide to slide
    const slidesLength = length * 2;
    let value = -position % slidesLength;
    if (direction > 0) {
        value = Math.ceil(value);
    }
    else if (direction < 0) {
        value = Math.floor(value);
    }
    // ensure that it loops
    if (value === slidesLength) {
        value = 0;
    }
    // handle negative direction
    if (value < 0) {
        value = slidesLength + value;
    }
    return value;
};
/**
 * 5 plaatjes , 100, 80, 200, 80, 100
 * 20 index, [100, 80, 200, 80, 100,100, 80, 200, 80, 100,100, 80, 200, 80, 100,100, 80, 200, 80, 100]
 * 18 index, [100, 80, 200, 80, 100,100, 80, 200, 80, 100,100, 80, 200, 80, 100, 100, 80, 200]
 *
 * 15 + 3,  15 === 3 total width, 3
 *
 * [0, 100, 180, 380, 460], total 560
 * offset [].reduce( + )
 */
export const offsetFromIndex = (slideWidths, isContainedVariant = false, isRTL = false) => {
    /**
     * Due to the animation direction difference (1 to -1)) the first image needed to be swapped but the slideWidths are calculated differently. While the content is duplicated (to ensure that all animations work in both directions (due to mirroring)) it is necessary to repeat the values 2 times.
     */
    const oneThird = Math.ceil(slideWidths.length / 3);
    const updatedSlideWidths = isContainedVariant && isRTL
        ? [
            ...slideWidths.slice(0, 1),
            ...slideWidths.slice(1, oneThird).reverse(),
            ...slideWidths.slice(0, 1),
            ...slideWidths.slice(1, oneThird).reverse(),
            ...slideWidths.slice(0, 1),
            ...slideWidths.slice(1, oneThird).reverse(),
        ]
        : slideWidths;
    const offsets = updatedSlideWidths.reduce((carry, value) => {
        carry.push((carry[carry.length - 1] || 0) + (isContainedVariant && isRTL ? -1 : 1) * value); // Responsive spacing
        return carry;
    }, [0]);
    const totalOffset = offsets.pop();
    return {
        offsets,
        totalOffset,
        createOffsetFromIndex: (index) => {
            const fullOffsets = Math.floor(index / slideWidths.length);
            const offsetOffset = index < 0
                ? (slideWidths.length - (Math.abs(index) % slideWidths.length)) % slideWidths.length
                : index % slideWidths.length;
            return fullOffsets * totalOffset + offsets[offsetOffset];
        },
    };
};
export const useOffsetFromIndex = (slideWidths, isContainedVariant = false, isRTL = false) => {
    const offsets = useRef();
    useEffect(() => slideWidths.on('change', (latestSlideWidths) => {
        offsets.current = offsetFromIndex(latestSlideWidths, isContainedVariant, isRTL);
    }), [slideWidths, isContainedVariant, isRTL]);
    const handleTotalOffset = useCallback(() => {
        var _a;
        return (_a = offsets.current) === null || _a === void 0 ? void 0 : _a.totalOffset;
    }, []);
    const handleCreateOffsetFromIndex = useCallback((index) => { var _a; return ((_a = offsets.current) === null || _a === void 0 ? void 0 : _a.createOffsetFromIndex(index)) || 0; }, []);
    return {
        createOffsetFromIndex: handleCreateOffsetFromIndex,
        totalOffset: handleTotalOffset,
    };
};
/**
 * This returns a array of offset for each slide so that from the current slide there are always slides on the left and on the right.
 * But without changing the order of the slides in the DOM
 *
 * Slides [0, 1, 2, 3, 4] with index on 0 returns [0, 100, 200, -200, -100]
 * This means that slide 0,1,2 are ordered normally, but 3 and 4 are position left of slide 1 (-200% and -100%).
 * This way when the user drags there are slides visible on both sides without the need to create duplicate slides or updating the DOM.
 */
export const calculateOffset = ({ slideWidths, contentLength, currentPage, splitAt, splitOffset, percentages = true, createOffsetFromIndex, totalOffset, isRTL = false, isContainedVariant = false, }) => {
    if (slideWidths.length === 0) {
        return Array(contentLength).fill(0);
    }
    // due to mirrored animation direction this needs to be different
    if (isContainedVariant && isRTL) {
        const leftContainedSide = Array(contentLength)
            .fill(null)
            .map((_, index) => percentages
            ? (index + (isRTL ? -1 : 1) * currentPage * contentLength) * 100
            : createOffsetFromIndex(contentLength - index + currentPage * contentLength, isRTL));
        const rightContainedSide = leftContainedSide.map((value) => 
        // due to the different direction (-1) the animation sometimes fails and therefore these workarounds are added
        contentLength % 2 === 0
            ? splitAt <= splitOffset
                ? value + Math.abs(totalOffset)
                : value + totalOffset
            : splitAt >= splitOffset // Left side is next page
                ? totalOffset + value
                : // Left side is previous page
                    value - totalOffset);
        return (contentLength % 2 === 0 ? splitAt <= splitOffset : splitAt < splitOffset)
            ? [
                ...rightContainedSide.slice(0, splitAt),
                ...leftContainedSide.slice(splitAt, contentLength),
            ]
            : [
                ...leftContainedSide.slice(0, splitAt),
                ...rightContainedSide.slice(splitAt, contentLength),
            ];
    }
    const rightSide = Array(contentLength)
        .fill(null)
        .map((_, index) => percentages
        ? (index + (isRTL ? -1 : 1) * currentPage * contentLength) * 100
        : createOffsetFromIndex(index + (isRTL ? -1 : 1) * currentPage * contentLength, isRTL));
    const leftSide = rightSide.map((value) => (isRTL ? splitAt > splitOffset : splitAt < splitOffset)
        ? // Left side is next page
            isRTL
                ? value - (percentages ? contentLength * 100 : totalOffset)
                : value + (percentages ? contentLength * 100 : totalOffset)
        : // Left side is previous page
            value - (percentages ? contentLength * 100 : totalOffset));
    return isRTL
        ? splitAt <= splitOffset
            ? [...rightSide.slice(0, splitAt), ...leftSide.slice(splitAt, contentLength)]
            : [...leftSide.slice(0, splitAt), ...rightSide.slice(splitAt, contentLength)]
        : splitAt >= splitOffset
            ? [...rightSide.slice(0, splitAt), ...leftSide.slice(splitAt, contentLength)]
            : [...leftSide.slice(0, splitAt), ...rightSide.slice(splitAt, contentLength)];
};
/**
 * Similar to useSpring that is already included in framer-motion but for a normal transition.
 * With some extra's so "set" instead of animate
 */
export const useTransition = (source, config = {}, directSetRef = null) => {
    const animatedMotionValue = useMotionValue(source.get());
    const directlySet = useRef(false);
    const animation = useRef();
    const handleDirect = useCallback((newValue) => {
        var _a;
        (_a = animation.current) === null || _a === void 0 ? void 0 : _a.stop();
        animatedMotionValue.set(newValue);
        directlySet.current = false;
    }, [animatedMotionValue]);
    const directlySetOnce = useCallback((value) => {
        directlySet.current = true;
        if (value !== undefined) {
            handleDirect(value);
        }
    }, [handleDirect]);
    const disableDirectlySetOnce = useCallback(() => {
        directlySet.current = false;
    }, []);
    useEffect(() => source.on('change', (newValue) => {
        var _a;
        if (directlySet.current || (directSetRef && directSetRef.current)) {
            handleDirect(newValue);
        }
        else {
            (_a = animation.current) === null || _a === void 0 ? void 0 : _a.stop();
            animation.current = animate(animatedMotionValue, newValue, {
                ...config,
                onUpdate: (latest) => {
                    var _a;
                    if ('onUpdate' in config) {
                        (_a = config === null || config === void 0 ? void 0 : config.onUpdate) === null || _a === void 0 ? void 0 : _a.call(config, latest);
                    }
                    if (directlySet.current) {
                        handleDirect(newValue);
                    }
                },
            });
        }
    }), [animatedMotionValue, config, directSetRef, handleDirect, source]);
    return { animatedMotionValue, directlySetOnce, disableDirectlySetOnce };
};
